import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Alert } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRegistrationSettings } from '../hooks/useRegistrationSettings';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase/client';

export interface CaptchaWidgetProps {
  action?: string;
}

export interface CaptchaWidgetRef {
  execute: () => Promise<{ token: string; version: 'v2' | 'v3'; score?: number } | null>;
  reset: () => void;
}

export const CaptchaWidget = forwardRef<CaptchaWidgetRef, CaptchaWidgetProps>(
  ({ action = 'signup' }, ref) => {
    const { captchaSettings, settings } = useRegistrationSettings();
    const [error, setError] = useState<string | null>(null);
    const [showV2, setShowV2] = useState(true); // Show v2 by default for visibility
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [v2Token, setV2Token] = useState<string | null>(null);

    // Expose execute method via ref
    useImperativeHandle(ref, () => ({
      execute: async () => {
        setError(null);

        if (!settings?.enableCaptcha) {
          return null;
        }

        // If v2 is showing and we have a token, return it
        if (showV2 && v2Token) {
          return { token: v2Token, version: 'v2' as const };
        }

        // If v2 is showing but no token yet, user needs to complete it
        if (showV2 && !v2Token) {
          setError('Please complete the CAPTCHA verification');
          return null;
        }

        setError('CAPTCHA not configured');
        return null;
      },
      reset: () => {
        setError(null);
        setV2Token(null);
        recaptchaRef.current?.reset();
      },
    }));

    const handleV2Change = async (token: string | null) => {
      if (!token) {
        return;
      }

      try {
        const validateCaptcha = httpsCallable<
          { token: string; action: string; version: 'v2' | 'v3' },
          { success: boolean }
        >(functions, 'validateCaptcha');

        const result = await validateCaptcha({
          token,
          action,
          version: 'v2',
        });

        if (result.data.success) {
          setError(null);
          setV2Token(token);
        } else {
          setError('CAPTCHA verification failed. Please try again.');
        }
      } catch (err) {
        console.error('reCAPTCHA v2 validation error:', err);
        setError('Failed to validate CAPTCHA. Please try again.');
      }
    };

    const handleV2Error = () => {
      setError('CAPTCHA error occurred. Please try again.');
    };

    if (!settings?.enableCaptcha) {
      return null;
    }

    return (
      <Box sx={{ my: 2 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {showV2 && captchaSettings?.reCaptchaV2SiteKey && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={captchaSettings.reCaptchaV2SiteKey}
              onChange={handleV2Change}
              onErrored={handleV2Error}
              onExpired={() => recaptchaRef.current?.reset()}
            />
          </Box>
        )}
      </Box>
    );
  },
);

CaptchaWidget.displayName = 'CaptchaWidget';

// Extend Window interface for reCAPTCHA
declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}
